/**
 * Spatial Audio Channel
 *
 * Processes audio for spatial positioning using Web Audio API
 */

import {Position} from "../../types/position";
import {
  SpatialDistanceConfig,
  DenoiserOptions,
  ParticipantAudioNodes,
  ListenerState,
  DEFAULT_SPATIAL_CONFIG,
  DEFAULT_DENOISER_OPTIONS,
} from "./SpatialAudioTypes";
import {getDistanceBetween} from "../../utils/spatial/distance-calc";
import {calculateLogarithmicGain} from "../../utils/spatial/gain-calc";
import {
  calculateListenerRight,
  calculatePanFromPositions,
} from "../../utils/spatial/pan-calc";
import {computeHeadPosition} from "../../utils/spatial/head-position";
import {normalizePositionUnits} from "../../utils/position/normalize";
import {
  createPositionSnapCache,
  PositionSnapCacheHandle,
} from "../../utils/position/snap";
import {
  createPanSmoother,
  PanSmootherHandle,
} from "../../utils/smoothing/pan-smoothing";
import {
  applyGainSmooth,
  applyStereoPanSmooth,
} from "../../utils/smoothing/gain-smoothing";
import {
  createMLNoiseSuppressor,
  MLNoiseSuppressorHandle,
} from "../../audio/MLNoiseSuppressor";
import {debugLog} from "../../utils/debug";

/**
 * Spatial audio configuration
 */
export interface SpatialAudioConfig {
  distance?: SpatialDistanceConfig;
  denoiser?: DenoiserOptions;
}
// ─── Factory handle interface ──────────────────────────────────────────────────

export interface SpatialAudioManagerHandle {
  getAudioContext(): AudioContext;
  setupParticipant(
    participantId: string,
    track: MediaStreamTrack,
    bypassSpatialization?: boolean
  ): Promise<void>;
  setupSpatialAudioForParticipant(
    participantId: string,
    track: MediaStreamTrack,
    bypassSpatialization?: boolean
  ): Promise<void>;
  updateSpatialAudio(
    participantId: string,
    position: Position,
    direction?: {x: number; y: number; z: number}
  ): void;
  setListenerFromLSD(
    listenerPos: Position,
    cameraPos: Position,
    lookAtPos: Position,
    rot?: {x: number; y: number; z: number}
  ): void;
  setListenerPosition(
    position: Position,
    orientation?: {
      forwardX: number;
      forwardY: number;
      forwardZ: number;
      upX: number;
      upY: number;
      upZ: number;
    }
  ): void;
  setParticipantSpatialization(
    participantId: string,
    enableSpatialization: boolean
  ): void;
  setParticipantMuted(participantId: string, muted: boolean): void;
  setMasterMuted(muted: boolean): void;
  getMasterMuted(): boolean;
  initializeMLNoiseSuppression(
    modelPath: string,
    workletUrl?: string
  ): Promise<void>;
  enhanceOutgoingAudioTrack(track: MediaStreamTrack): Promise<MediaStreamTrack>;
  getNoiseSuppressionMode(): "ml" | "audioworklet" | "none";
  isMLModelLoaded(): boolean;
  removeParticipant(participantId: string): void;
  resumeAudioContext(): Promise<void>;
  getAudioContextState(): AudioContextState;
  getParticipantAudioLevel(participantId: string): number;
  isParticipantSpeaking(participantId: string, threshold?: number): boolean;
  getActiveParticipants(): string[];
  hasParticipant(participantId: string): boolean;
  getParticipantCount(): number;
}

// ─── Factory function ─────────────────────────────────────────────────────────────────────────

/**
 * Create a spatial audio manager.
 * All state is held in closure variables — no class instantiation needed.
 */
export function createSpatialAudioManager(
  config?: SpatialAudioConfig
): SpatialAudioManagerHandle {
  // ── Configuration ─────────────────────────────────────────────────────────
  const distanceConfig: Required<SpatialDistanceConfig> = {
    ...DEFAULT_SPATIAL_CONFIG,
    ...config?.distance,
  };
  const denoiserConfig: Required<DenoiserOptions> = {
    ...DEFAULT_DENOISER_OPTIONS,
    ...config?.denoiser,
  };

  // ── AudioContext + master chain ───────────────────────────────────────────
  const audioContext = new AudioContext({sampleRate: 48000});

  // Chrome suspends AudioContext created outside a user-gesture frame.
  const resumeOnGesture = () => {
    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {
        /* ignore */
      });
    }
    document.removeEventListener("click", resumeOnGesture);
    document.removeEventListener("keydown", resumeOnGesture);
    document.removeEventListener("touchstart", resumeOnGesture);
  };
  document.addEventListener("click", resumeOnGesture);
  document.addEventListener("keydown", resumeOnGesture);
  document.addEventListener("touchstart", resumeOnGesture);

  // Master gain raised to 2.5 to compensate for ML IRM attenuation.
  const masterGainNode = audioContext.createGain();
  masterGainNode.gain.value = 2.5;

  // Master compressor — DISABLED for spatial audio (ratio 1:1 = pass-through)
  const masterCompressor = audioContext.createDynamicsCompressor();
  masterCompressor.threshold.value = 0;
  masterCompressor.knee.value = 0;
  masterCompressor.ratio.value = 1;
  masterCompressor.attack.value = 0.003;
  masterCompressor.release.value = 0.25;

  masterGainNode.connect(masterCompressor);
  masterCompressor.connect(audioContext.destination);

  // ── Per-participant state ──────────────────────────────────────────────────
  const participantNodes = new Map<string, ParticipantAudioNodes>();

  const listenerState: ListenerState = {
    position: {x: 0, y: 0, z: 0},
    right: {x: 1, z: 0},
    initialized: false,
  };

  const positionCache = createPositionSnapCache(0.3);
  const panSmoother = createPanSmoother();
  let isMasterMuted = false;

  const lastGainValues = new Map<string, number>();
  const lastPanValues = new Map<string, number>();
  const GAIN_CHANGE_THRESHOLD = 0.02;
  const PAN_CHANGE_THRESHOLD = 0.03;
  const lastUpdateTime = new Map<string, number>();
  const MIN_UPDATE_INTERVAL_MS = 50;

  // ── ML / AudioWorklet state ───────────────────────────────────────────────
  let mlNoiseSuppressor: MLNoiseSuppressorHandle | null = null;
  let noiseSuppressionMode: "ml" | "audioworklet" | "none" = "none";
  const inferenceInFlight = new Set<string>();
  let workletRegistered = false;
  let workletUrl: string | null = null;

  type OutgoingChain = {
    inputTrack: MediaStreamTrack;
    outputTrack: MediaStreamTrack;
    source: MediaStreamAudioSourceNode;
    denoiseNode: AudioWorkletNode;
    destination: MediaStreamAudioDestinationNode;
  };
  let outgoingDenoiseChain: OutgoingChain | null = null;

  // ── Private helpers ────────────────────────────────────────────────────────
  function createPanner(): PannerNode {
    const panner = audioContext.createPanner();
    panner.panningModel = "HRTF";
    panner.distanceModel = "inverse";
    panner.refDistance = distanceConfig.refDistance;
    panner.maxDistance = distanceConfig.maxDistance;
    panner.rolloffFactor = distanceConfig.rolloffFactor;
    panner.coneInnerAngle = 360;
    panner.coneOuterAngle = 360;
    panner.coneOuterGain = 0.3;
    return panner;
  }

  function createMonoChain() {
    const monoSplitter = audioContext.createChannelSplitter(2);
    const monoGainL = audioContext.createGain();
    const monoGainR = audioContext.createGain();
    const monoMerger = audioContext.createChannelMerger(1);
    const stereoUpmixer = audioContext.createChannelMerger(2);
    monoGainL.gain.value = 0.5;
    monoGainR.gain.value = 0.5;
    return {monoSplitter, monoGainL, monoGainR, monoMerger, stereoUpmixer};
  }

  function createParticipantCompressor(): DynamicsCompressorNode {
    const comp = audioContext.createDynamicsCompressor();
    comp.threshold.value = -30;
    comp.knee.value = 12;
    comp.ratio.value = 3;
    comp.attack.value = 0.003;
    comp.release.value = 0.15;
    return comp;
  }

  function createFilters() {
    const highpassFilter = audioContext.createBiquadFilter();
    highpassFilter.type = "highpass";
    highpassFilter.frequency.value = 100;
    highpassFilter.Q.value = 0.5;

    const lowpassFilter = audioContext.createBiquadFilter();
    lowpassFilter.type = "lowpass";
    lowpassFilter.frequency.value = 10000;
    lowpassFilter.Q.value = 0.5;

    const voiceBandFilter = audioContext.createBiquadFilter();
    voiceBandFilter.type = "peaking";
    voiceBandFilter.frequency.value = 180;
    voiceBandFilter.Q.value = 0.5;
    voiceBandFilter.gain.value = 0;

    const dynamicLowpass = audioContext.createBiquadFilter();
    dynamicLowpass.type = "lowpass";
    dynamicLowpass.frequency.value = 12000;
    dynamicLowpass.Q.value = 0.5;

    return {highpassFilter, lowpassFilter, voiceBandFilter, dynamicLowpass};
  }

  function teardownOutgoingDenoiseChain(
    chain: OutgoingChain | null = outgoingDenoiseChain
  ): void {
    const localPid = "__local_outgoing__";
    inferenceInFlight.delete(localPid);
    if (mlNoiseSuppressor) {
      mlNoiseSuppressor.clearParticipantSmoothingState(localPid);
    }
    if (!chain) return;
    try {
      chain.denoiseNode.port.close();
    } catch {
      /* ignore */
    }
    try {
      chain.source.disconnect();
    } catch {
      /* ignore */
    }
    try {
      chain.denoiseNode.disconnect();
    } catch {
      /* ignore */
    }
    if (outgoingDenoiseChain === chain) {
      outgoingDenoiseChain = null;
    }
  }

  // ── Public methods ─────────────────────────────────────────────────────────

  function getAudioContext(): AudioContext {
    return audioContext;
  }

  async function setupParticipant(
    participantId: string,
    track: MediaStreamTrack,
    bypassSpatialization: boolean = false
  ): Promise<void> {
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }

    const stream = new MediaStream([track]);
    const source = audioContext.createMediaStreamSource(stream);
    const panner = createPanner();
    const stereoPanner = audioContext.createStereoPanner();
    const {monoSplitter, monoGainL, monoGainR, monoMerger, stereoUpmixer} =
      createMonoChain();
    const analyser = audioContext.createAnalyser();
    const gain = audioContext.createGain();
    const proximityGain = audioContext.createGain();
    const comp = createParticipantCompressor();
    const {highpassFilter, lowpassFilter, voiceBandFilter, dynamicLowpass} =
      createFilters();

    gain.gain.value = 1.0;
    proximityGain.gain.value = 1.0;

    let noiseNode: AudioWorkletNode | null = null;

    if (workletRegistered && mlNoiseSuppressor) {
      const cfg = mlNoiseSuppressor.getModelConfig();
      noiseNode = new AudioWorkletNode(audioContext, "noise-suppressor", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });

      noiseNode.port.postMessage({
        type: "config",
        n_fft: cfg?.n_fft ?? 512,
        hop_length: cfg?.hop_length ?? 80,
        n_mels: cfg?.n_mels ?? 40,
        sample_rate: cfg?.sample_rate ?? 16000,
        sequence_length: cfg?.sequence_length ?? 8,
      });

      const workletRef = noiseNode;
      const seqLen = cfg?.sequence_length ?? 8;
      const nMels = cfg?.n_mels ?? 40;
      const _pid = participantId;
      noiseNode.port.onmessage = async (e: MessageEvent) => {
        if (e.data.type === "mel_features" && mlNoiseSuppressor?.isReady()) {
          if (inferenceInFlight.has(_pid)) return;
          inferenceInFlight.add(_pid);
          try {
            const gains = await mlNoiseSuppressor.computeGainsFromFeatures(
              new Float32Array(e.data.features),
              seqLen,
              nMels,
              _pid
            );
            try {
              workletRef.port.postMessage({type: "gains", gains}, [
                gains.buffer,
              ]);
            } catch {
              /* worklet disconnected */
            }
          } finally {
            inferenceInFlight.delete(_pid);
          }
        }
      };
    }

    if (noiseNode) {
      source.connect(noiseNode);
      noiseNode.connect(comp);
    } else {
      source.connect(comp);
    }

    comp.connect(highpassFilter);
    highpassFilter.connect(voiceBandFilter);
    voiceBandFilter.connect(lowpassFilter);
    lowpassFilter.connect(dynamicLowpass);
    dynamicLowpass.connect(proximityGain);

    proximityGain.connect(monoSplitter);
    monoSplitter.connect(monoGainL, 0);
    monoSplitter.connect(monoGainR, 1);
    monoGainL.connect(monoMerger, 0, 0);
    monoGainR.connect(monoMerger, 0, 0);
    monoMerger.connect(stereoUpmixer, 0, 0);
    monoMerger.connect(stereoUpmixer, 0, 1);
    stereoUpmixer.connect(analyser);

    if (bypassSpatialization) {
      analyser.connect(gain);
    } else {
      analyser.connect(stereoPanner);
      stereoPanner.connect(gain);
    }
    gain.connect(masterGainNode);

    participantNodes.set(participantId, {
      source,
      panner,
      stereoPanner,
      monoSplitter,
      monoGainL,
      monoGainR,
      monoMerger,
      stereoUpmixer,
      analyser,
      gain,
      proximityGain,
      compressor: comp,
      highpassFilter,
      lowpassFilter,
      voiceBandFilter,
      dynamicLowpass,
      denoiseNode: noiseNode ?? undefined,
      stream,
    });

    lastGainValues.set(participantId, 1.0);
    lastPanValues.set(participantId, 0);
  }

  function updateSpatialAudio(
    participantId: string,
    position: Position,
    direction?: {x: number; y: number; z: number}
  ): void {
    const nodes = participantNodes.get(participantId);
    if (!nodes?.panner) return;

    if (!listenerState.initialized) return;

    const now = performance.now();
    const lastTime = lastUpdateTime.get(participantId) || 0;
    if (now - lastTime < MIN_UPDATE_INTERVAL_MS) return;
    lastUpdateTime.set(participantId, now);

    const normalizedPos = normalizePositionUnits(position, distanceConfig.unit);
    const snappedPos = positionCache.snap(normalizedPos, participantId);
    const speakerHead = computeHeadPosition(snappedPos);

    const listenerPos = listenerState.position;
    const distance = getDistanceBetween(listenerPos, speakerHead);

    if (distance >= distanceConfig.maxDistance) {
      const lastGain = lastGainValues.get(participantId) ?? 1.0;
      if (lastGain > 0.01) {
        applyGainSmooth(nodes.gain, 0, audioContext, 0.1);
        lastGainValues.set(participantId, 0);
      }
      return;
    }

    const rawPan = calculatePanFromPositions(
      listenerPos,
      speakerHead,
      listenerState.right
    );
    const smoothedPan = panSmoother.smooth(participantId, rawPan);

    const gainPercent = calculateLogarithmicGain(distance, {
      minDistance: distanceConfig.refDistance,
      maxDistance: distanceConfig.maxDistance,
    });
    const gainValue = gainPercent / 100;
    const panValue = smoothedPan;

    const lastPan = lastPanValues.get(participantId) ?? 0;
    const lastGain = lastGainValues.get(participantId) ?? 1.0;
    const panChanged = Math.abs(panValue - lastPan) > PAN_CHANGE_THRESHOLD;
    const gainChanged = Math.abs(gainValue - lastGain) > GAIN_CHANGE_THRESHOLD;

    if (panChanged || gainChanged) {
      const panDirection =
        panValue > 0.3 ? "RIGHT" : panValue < -0.3 ? "LEFT" : "CENTER";
      const panPercent = Math.abs(panValue * 100).toFixed(0);

      debugLog("SPATIAL", `${participantId.slice(0, 8)}`, {
        dist: distance.toFixed(1) + "m",
        gain: (gainValue * 100).toFixed(0) + "%",
        pan: `${panPercent}% ${panDirection}`,
        rawPan: rawPan.toFixed(2),
        listenerRight: `(${listenerState.right.x.toFixed(2)}, ${listenerState.right.z.toFixed(2)})`,
      });
    }

    if (panChanged) {
      applyStereoPanSmooth(nodes.stereoPanner, panValue, audioContext, 0.08);
      lastPanValues.set(participantId, panValue);
    }

    const GAIN_THRESHOLD = 0.025;
    const isFirstCalculation = !lastGainValues.has(participantId);
    const significantChange = Math.abs(gainValue - lastGain) > GAIN_THRESHOLD;

    if (isFirstCalculation || significantChange) {
      applyGainSmooth(nodes.gain, gainValue, audioContext, 0.15);
      lastGainValues.set(participantId, gainValue);
    }
  }

  function setListenerFromLSD(
    listenerPos: Position,
    cameraPos: Position,
    lookAtPos: Position,
    rot?: {x: number; y: number; z: number}
  ): void {
    const normalizedListener = normalizePositionUnits(
      cameraPos,
      distanceConfig.unit
    );
    const snappedListener = positionCache.snap(normalizedListener, "listener");

    listenerState.position = snappedListener;
    listenerState.initialized = true;

    if (rot && typeof rot.y === "number") {
      listenerState.right = calculateListenerRight(rot.y);
    } else if (lookAtPos && cameraPos) {
      const dx = lookAtPos.x - cameraPos.x;
      const dz = lookAtPos.z - cameraPos.z;
      const yawRadians = Math.atan2(dx, dz);
      const yawDegrees = (yawRadians * 180) / Math.PI;
      listenerState.right = calculateListenerRight(yawDegrees);
    }
  }

  function setParticipantSpatialization(
    participantId: string,
    enableSpatialization: boolean
  ): void {
    const nodes = participantNodes.get(participantId);
    if (!nodes) {
      console.warn(
        `[SpatialAudioChannel] Cannot set spatialization - no nodes for participant: ${participantId}`
      );
      return;
    }

    try {
      const currentTime = audioContext.currentTime;
      const fadeTime = 0.03;

      if (enableSpatialization) {
        nodes.analyser.connect(nodes.stereoPanner);
        nodes.stereoPanner.connect(nodes.gain);
        setTimeout(() => {
          try {
            /* already routed */
          } catch (e) {
            /* ignore */
          }
        }, fadeTime * 1000);
      } else {
        nodes.analyser.connect(nodes.gain);
        nodes.gain.gain.setTargetAtTime(1.0, currentTime, fadeTime);
        nodes.stereoPanner.pan.setTargetAtTime(0, currentTime, fadeTime);
        setTimeout(() => {
          try {
            nodes.stereoPanner.disconnect();
          } catch (e) {
            /* already disconnected */
          }
        }, fadeTime * 1000);
      }
    } catch (error) {
      console.error(
        `[SpatialAudioChannel] Error setting spatialization for ${participantId}:`,
        error
      );
    }
  }

  function setParticipantMuted(participantId: string, muted: boolean): void {
    const nodes = participantNodes.get(participantId);
    if (!nodes?.gain) return;
    applyGainSmooth(nodes.gain, muted ? 0 : 1, audioContext, 0.05);
  }

  function setMasterMuted(muted: boolean): void {
    isMasterMuted = muted;
    applyGainSmooth(masterGainNode, muted ? 0 : 1, audioContext, 0.05);
  }

  function getMasterMuted(): boolean {
    return isMasterMuted;
  }

  function setListenerPosition(
    position: Position,
    orientation?: {
      forwardX: number;
      forwardY: number;
      forwardZ: number;
      upX: number;
      upY: number;
      upZ: number;
    }
  ): void {
    const normalizedPosition = normalizePositionUnits(position);
    positionCache.snap(normalizedPosition, "listener");
    listenerState.position = normalizedPosition;

    if (orientation) {
      const yawRadians = Math.atan2(orientation.forwardX, orientation.forwardZ);
      const yawDegrees = (yawRadians * 180) / Math.PI;
      listenerState.right = calculateListenerRight(yawDegrees);
    }

    listenerState.initialized = true;
  }

  async function setupSpatialAudioForParticipant(
    participantId: string,
    track: MediaStreamTrack,
    bypassSpatialization?: boolean
  ): Promise<void> {
    return setupParticipant(
      participantId,
      track,
      bypassSpatialization || false
    );
  }

  async function initializeMLNoiseSuppression(
    modelPath: string,
    workletUrlParam?: string
  ): Promise<void> {
    if (workletUrlParam && !workletRegistered) {
      try {
        await audioContext.audioWorklet.addModule(workletUrlParam);
        workletRegistered = true;
        workletUrl = workletUrlParam;
        console.log(
          `[SpatialAudioChannel] AudioWorklet registered: ${workletUrlParam}`
        );
      } catch (e) {
        console.warn(
          "[SpatialAudioChannel] AudioWorklet registration failed — falling back to passthrough:",
          e
        );
      }
    }

    mlNoiseSuppressor = createMLNoiseSuppressor();
    await mlNoiseSuppressor.initialize(modelPath);

    if (!mlNoiseSuppressor.isReady()) {
      mlNoiseSuppressor = null;
      throw new Error(
        `[SpatialAudioChannel] ML model loaded from ${modelPath} but isReady() returned false`
      );
    }

    noiseSuppressionMode = "ml";
    console.log(
      `[SpatialAudioChannel] ML noise suppression ACTIVE — model loaded from ${modelPath}`
    );
  }

  async function enhanceOutgoingAudioTrack(
    track: MediaStreamTrack
  ): Promise<MediaStreamTrack> {
    if (track.kind !== "audio") return track;
    if (!workletRegistered || !mlNoiseSuppressor?.isReady()) return track;

    if (
      outgoingDenoiseChain &&
      outgoingDenoiseChain.outputTrack.id === track.id &&
      outgoingDenoiseChain.outputTrack.readyState === "live"
    ) {
      return outgoingDenoiseChain.outputTrack;
    }

    if (
      outgoingDenoiseChain &&
      outgoingDenoiseChain.inputTrack.id === track.id &&
      outgoingDenoiseChain.outputTrack.readyState === "live"
    ) {
      return outgoingDenoiseChain.outputTrack;
    }

    const cfg = mlNoiseSuppressor.getModelConfig();
    if (!cfg) return track;

    try {
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const previousChain = outgoingDenoiseChain;
      const stream = new MediaStream([track]);
      const source = audioContext.createMediaStreamSource(stream);
      const denoiseNode = new AudioWorkletNode(
        audioContext,
        "noise-suppressor",
        {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1],
        }
      );
      const destination = audioContext.createMediaStreamDestination();

      denoiseNode.port.postMessage({
        type: "config",
        n_fft: cfg.n_fft ?? 512,
        hop_length: cfg.hop_length ?? 80,
        n_mels: cfg.n_mels ?? 40,
        sample_rate: cfg.sample_rate ?? 16000,
        sequence_length: cfg.sequence_length ?? 8,
      });

      const seqLen = cfg.sequence_length ?? 8;
      const nMels = cfg.n_mels ?? 40;
      const localPid = "__local_outgoing__";
      const workletRef = denoiseNode;

      denoiseNode.port.onmessage = async (e: MessageEvent) => {
        if (e.data.type === "mel_features" && mlNoiseSuppressor?.isReady()) {
          if (inferenceInFlight.has(localPid)) return;
          inferenceInFlight.add(localPid);
          try {
            const gains = await mlNoiseSuppressor.computeGainsFromFeatures(
              new Float32Array(e.data.features),
              seqLen,
              nMels,
              localPid
            );
            workletRef.port.postMessage({type: "gains", gains}, [gains.buffer]);
          } catch {
            // Keep last known gains when inference fails for one frame.
          } finally {
            inferenceInFlight.delete(localPid);
          }
        }
      };

      source.connect(denoiseNode);
      denoiseNode.connect(destination);

      const enhancedTrack = destination.stream.getAudioTracks()[0];
      if (!enhancedTrack) {
        try {
          source.disconnect();
        } catch {
          /* ignore */
        }
        try {
          denoiseNode.disconnect();
        } catch {
          /* ignore */
        }
        return track;
      }

      enhancedTrack.enabled = track.enabled;

      track.addEventListener("ended", () => teardownOutgoingDenoiseChain(), {
        once: true,
      });
      enhancedTrack.addEventListener(
        "ended",
        () => teardownOutgoingDenoiseChain(),
        {once: true}
      );

      outgoingDenoiseChain = {
        inputTrack: track,
        outputTrack: enhancedTrack,
        source,
        denoiseNode,
        destination,
      };

      if (previousChain) {
        teardownOutgoingDenoiseChain(previousChain);
      }

      console.log(
        "[SpatialAudioChannel] Local outgoing ML denoise ACTIVE (AudioWorklet)"
      );
      return enhancedTrack;
    } catch (e) {
      console.warn(
        "[SpatialAudioChannel] Failed to enhance outgoing track, using original track:",
        e
      );
      return track;
    }
  }

  function getNoiseSuppressionMode(): "ml" | "audioworklet" | "none" {
    return noiseSuppressionMode;
  }

  function isMLModelLoaded(): boolean {
    return mlNoiseSuppressor?.isReady() ?? false;
  }

  function removeParticipant(participantId: string): void {
    const nodes = participantNodes.get(participantId);
    if (nodes) {
      debugLog("AUDIO", `Removing participant: ${participantId.slice(0, 8)}`, {
        totalBefore: participantNodes.size,
        totalAfter: participantNodes.size - 1,
      });

      nodes.denoiseNode?.port.close();
      nodes.denoiseNode?.disconnect();
      nodes.source.disconnect();
      nodes.panner.disconnect();
      nodes.stereoPanner.disconnect();
      nodes.analyser.disconnect();
      nodes.gain.disconnect();
      participantNodes.delete(participantId);
      panSmoother.clear(participantId);
      positionCache.clear(participantId);
      lastGainValues.delete(participantId);
      lastPanValues.delete(participantId);
      lastUpdateTime.delete(participantId);
      inferenceInFlight.delete(participantId);
      if (mlNoiseSuppressor) {
        mlNoiseSuppressor.clearParticipantSmoothingState(participantId);
      }
    } else {
      debugLog(
        "AUDIO",
        `Remove called but participant not found: ${participantId.slice(0, 8)}`
      );
    }
  }

  async function resumeAudioContext(): Promise<void> {
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
  }

  function getAudioContextState(): AudioContextState {
    return audioContext.state;
  }

  function getParticipantAudioLevel(participantId: string): number {
    const nodes = participantNodes.get(participantId);
    if (!nodes?.analyser) return 0;

    const dataArray = new Uint8Array(nodes.analyser.frequencyBinCount);
    nodes.analyser.getByteFrequencyData(dataArray);

    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    const rms = Math.sqrt(sum / dataArray.length);
    return Math.min(100, Math.round((rms / 255) * 100));
  }

  function isParticipantSpeaking(
    participantId: string,
    threshold: number = 5
  ): boolean {
    return getParticipantAudioLevel(participantId) > threshold;
  }

  function getActiveParticipants(): string[] {
    return Array.from(participantNodes.keys());
  }

  function hasParticipant(participantId: string): boolean {
    return participantNodes.has(participantId);
  }

  function getParticipantCount(): number {
    return participantNodes.size;
  }

  // ── Return handle ──────────────────────────────────────────────────────────
  return {
    getAudioContext,
    setupParticipant,
    setupSpatialAudioForParticipant,
    updateSpatialAudio,
    setListenerFromLSD,
    setListenerPosition,
    setParticipantSpatialization,
    setParticipantMuted,
    setMasterMuted,
    getMasterMuted,
    initializeMLNoiseSuppression,
    enhanceOutgoingAudioTrack,
    getNoiseSuppressionMode,
    isMLModelLoaded,
    removeParticipant,
    resumeAudioContext,
    getAudioContextState,
    getParticipantAudioLevel,
    isParticipantSpeaking,
    getActiveParticipants,
    hasParticipant,
    getParticipantCount,
  };
}

export type {SpatialAudioConfig as SpatialAudioOptions};
